import * as React from "react";
import { ChevronDown, ChevronUp, Home } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Dock, DockIcon } from "@repo/ui/components/dock";
import { Separator } from "@repo/ui/primitives/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui/primitives/tooltip";

import { sidebarSubItemMatchesPath } from "@/config/sidebar-nav.utils.js";
import { cn } from "@/lib/utils";
import { prefetchNamespacesForPath } from "@/lib/i18n-route-namespaces.js";
import { useDockPreferencesStore } from "@/store/dock-preferences.store.js";

function DockLink({ active, icon, label, url }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={url}
          aria-label={label}
          aria-current={active ? "page" : undefined}
          onClick={() => void prefetchNamespacesForPath(url)}
          className={cn(
"flex h-full min-h-[var(--ds-density-control-height-xl)] w-full min-w-[var(--ds-density-control-height-xl)] items-center justify-center rounded-[var(--ds-radius-control)] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none",
            active
              ? "border-primary/40 bg-primary text-primary-foreground shadow-[var(--shadow-surface)]"
              : "border-border bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          )}
        >
          {React.createElement(icon, {
            className: "size-[45%] min-h-4 min-w-4",
"aria-hidden": true,
          })}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={10}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function DockCollapseButton({ expanded, preferenceKey, t }) {
  const setExpanded = useDockPreferencesStore((state) => state.setExpanded);
  const label = expanded
    ? t("settings.dock.collapse", { defaultValue: "Collapse dock", ns: "header" })
    : t("settings.dock.expand", { defaultValue: "Open dock", ns: "header" });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-expanded={expanded}
          aria-controls={expanded ? "dashboard-quick-access-dock" : undefined}
          onClick={() => setExpanded(preferenceKey, !expanded)}
          className="pointer-events-auto flex h-[var(--ds-density-control-height-lg)] w-12 items-center justify-center rounded-[var(--ds-radius-pill)] border border-border bg-card text-muted-foreground shadow-[var(--shadow-popover)] transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
        >
          {expanded ? (
            <ChevronDown className="size-3.5" aria-hidden="true" />
          ) : (
            <ChevronUp className="size-4" aria-hidden="true" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={expanded ? 6 : 8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function DashboardDock({
  pages,
  preferenceKey,
  preferences,
  showCollapse = false,
}) {
  const location = useLocation();
  const { t } = useTranslation(["header", "sidebar"]);

  const pagesByUrl = React.useMemo(
    () => new Map((pages || []).map((page) => [page.url, page])),
    [pages],
  );

  const shortcuts = React.useMemo(
    () => preferences.slots.map((url) => pagesByUrl.get(url)).filter(Boolean),
    [pagesByUrl, preferences.slots],
  );

  if (!preferenceKey || !preferences.enabled) return null;

  const expanded = showCollapse ? preferences.expanded !== false : true;

  return (
    <div
      className="group/quick-dock pointer-events-none absolute inset-x-0 bottom-0 z-40 flex justify-center"
      data-dashboard-dock="hover"
    >
      <div
        className="pointer-events-auto absolute inset-x-0 bottom-0 h-3"
        aria-hidden="true"
        data-dashboard-dock="hover-target"
      />
      <div
        className="invisible flex w-full min-w-0 flex-col items-center px-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] opacity-0 transition-opacity duration-150 group-hover/quick-dock:pointer-events-auto group-hover/quick-dock:visible group-hover/quick-dock:opacity-100 focus-within:pointer-events-auto focus-within:visible focus-within:opacity-100 sm:px-4"
        data-dashboard-dock="surface"
      >
        <TooltipProvider delayDuration={150}>
          {expanded ? (
            <div className="flex w-full min-w-0 flex-col items-center gap-1">
              <nav
                id="dashboard-quick-access-dock"
                aria-label={t("settings.dock.title", { defaultValue: "Quick Access Dock", ns: "header" })}
                className="w-full max-w-full overflow-x-auto overflow-y-hidden no-scrollbar"
              >
                <div className="flex w-max min-w-full justify-center px-5 py-1">
                  <Dock
                    iconSize={44}
                    iconMagnification={58}
                    iconDistance={120}
                    className="h-16 shrink-0 gap-1 rounded-[var(--ds-radius-workbench)] border border-border bg-card/95 shadow-[var(--shadow-surface)]"
                  >
                    <DockIcon>
                      <DockLink
                        active={location.pathname === "/home"}
                        icon={Home}
                        label={t("nav.home", { defaultValue: "Home", ns: "sidebar" })}
                        url="/home"
                      />
                    </DockIcon>

                    {shortcuts.length > 0 && (
                      <Separator orientation="vertical" className="mx-1 h-8 self-center" />
                    )}

                    {shortcuts.map((page) => (
                      <DockIcon key={page.url}>
                        <DockLink
                          active={sidebarSubItemMatchesPath(location.pathname, page)}
                          icon={page.icon}
                          label={page.title}
                          url={page.url}
                        />
                      </DockIcon>
                    ))}
                  </Dock>
                </div>
              </nav>
              {showCollapse ? (
                <DockCollapseButton
                  expanded
                  preferenceKey={preferenceKey}
                  t={t}
                />
              ) : null}
            </div>
          ) : (
            <DockCollapseButton
              expanded={false}
              preferenceKey={preferenceKey}
              t={t}
            />
          )}
        </TooltipProvider>
      </div>
    </div>
  );
}
